/**
 * "Embed in your app" — Widget section (ADR-0074).
 * The end-to-end install journey: get the key, drop the tag, wire it into an
 * SPA, mount inline, send identity, satisfy a strict CSP. Reconciled from
 * apps/widget bootstrap (config.ts, main.ts) + the @syncanix/shared embed
 * snippet SoT; the `<script>` blocks are guarded by
 * `docs-embed-snippet-truth.test.ts` against the real attribute contract.
 */
import type { DocPageContent } from '../types';

const SCRIPT = `<script
  type="module"
  src="https://cdn.syncanix.com/widget.js"
  data-key="pk_test_..."
  data-position="bottom-right"
  data-env="development"
></script>`;

const REACT = `import { useEffect } from 'react';

export function SyncanixWidget({ embedKey }: { embedKey: string }) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'module';
    script.src = 'https://cdn.syncanix.com/widget.js';
    script.setAttribute('data-key', embedKey);
    document.body.appendChild(script);
    return () => {
      window.syncanix?.unmount();
      script.remove();
    };
  }, [embedKey]);
  return null;
}`;

const INLINE = `<div id="assistant-panel"></div>

<script
  type="module"
  src="https://cdn.syncanix.com/widget.js"
  data-key="pk_live_..."
  data-chat-size="embedded"
  data-mount-target="#assistant-panel"
></script>`;

const TOKEN = `window.syncanix?.setTokenProvider(async () => {
  // Called before each turn — return the signed-in user's current token.
  return auth.getTokenSilently();
});`;

const CSP = `script-src  https://cdn.syncanix.com;
connect-src https://api.syncanix.com;
style-src   'unsafe-inline';`;

export const WIDGET_EMBEDDING_PAGE: DocPageContent = {
  slug: 'widget/embedding',
  titleKey: 'docs.widget.embedding.title',
  descriptionKey: 'docs.widget.embedding.description',
  blocks: [
    { kind: 'paragraph', textKey: 'docs.widget.embedding.lead' },

    { kind: 'heading', level: 2, id: 'key', textKey: 'docs.widget.embedding.key.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.embedding.key.body' },
    {
      kind: 'steps',
      steps: [
        {
          titleKey: 'docs.widget.embedding.key.step.open.title',
          textKey: 'docs.widget.embedding.key.step.open.text',
        },
        {
          titleKey: 'docs.widget.embedding.key.step.copy.title',
          textKey: 'docs.widget.embedding.key.step.copy.text',
        },
        {
          titleKey: 'docs.widget.embedding.key.step.env.title',
          textKey: 'docs.widget.embedding.key.step.env.text',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.widget.embedding.key.tip.title',
      textKey: 'docs.widget.embedding.key.tip.body',
      links: {
        envs: {
          target: { docSlug: 'dashboard/environments' },
          labelKey: 'docs.widget.embedding.key.tip.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'script', textKey: 'docs.widget.embedding.script.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.embedding.script.body' },
    { kind: 'code', language: 'html', code: SCRIPT },
    {
      kind: 'paragraph',
      textKey: 'docs.widget.embedding.script.note',
      links: {
        config: {
          target: { docSlug: 'widget/configuration' },
          labelKey: 'docs.widget.embedding.script.note.link',
        },
      },
    },

    {
      // Z-2, same sentence as the quickstart's embed step — one key, two render
      // sites, so the public install narrative cannot drift into two different
      // accounts of the one precondition that decides whether the widget mounts.
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.widget.origins.callout.title',
      textKey: 'docs.widget.origins.callout.body',
      links: {
        connections: {
          target: { docSlug: 'dashboard/connections' },
          labelKey: 'docs.widget.origins.callout.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'spa', textKey: 'docs.widget.embedding.spa.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.embedding.spa.body' },
    { kind: 'code', language: 'tsx', code: REACT },
    { kind: 'paragraph', textKey: 'docs.widget.embedding.spa.frameworks' },
    {
      kind: 'callout',
      tone: 'note',
      titleKey: 'docs.widget.embedding.spa.ssr.title',
      textKey: 'docs.widget.embedding.spa.ssr.body',
    },

    { kind: 'heading', level: 2, id: 'inline', textKey: 'docs.widget.embedding.inline.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.embedding.inline.body' },
    { kind: 'code', language: 'html', code: INLINE },

    {
      kind: 'heading',
      level: 2,
      id: 'identity',
      textKey: 'docs.widget.embedding.identity.heading',
    },
    {
      kind: 'paragraph',
      textKey: 'docs.widget.embedding.identity.body',
      links: {
        auth: {
          target: { docSlug: 'auth/end-user-auth' },
          labelKey: 'docs.widget.embedding.identity.link',
        },
      },
    },
    { kind: 'code', language: 'js', code: TOKEN },

    { kind: 'heading', level: 2, id: 'csp', textKey: 'docs.widget.embedding.csp.heading' },
    { kind: 'paragraph', textKey: 'docs.widget.embedding.csp.body' },
    { kind: 'code', language: 'text', code: CSP },
    {
      kind: 'list',
      itemKeys: [
        'docs.widget.embedding.csp.item.script',
        'docs.widget.embedding.csp.item.connect',
        'docs.widget.embedding.csp.item.style',
        'docs.widget.embedding.csp.item.trustedTypes',
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      titleKey: 'docs.widget.embedding.verify.title',
      textKey: 'docs.widget.embedding.verify.body',
      links: {
        troubleshooting: {
          target: { docSlug: 'guides/troubleshooting' },
          labelKey: 'docs.widget.embedding.verify.link',
        },
      },
    },

    { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
    {
      kind: 'cardGrid',
      cards: [
        {
          titleKey: 'docs.widget.embedding.next.config.title',
          textKey: 'docs.widget.embedding.next.config.text',
          to: { docSlug: 'widget/configuration' },
        },
        {
          titleKey: 'docs.widget.embedding.next.auth.title',
          textKey: 'docs.widget.embedding.next.auth.text',
          to: { docSlug: 'auth/end-user-auth' },
        },
      ],
    },
  ],
};
